/* eslint-disable eqeqeq */

import { useContext, useMemo, useState } from "react";
import { useForm } from "@mantine/form";
import * as XLSX from 'xlsx';

import {
    Alert,
    Checkbox,
    Group,
    Modal,
    MultiSelect,
    Radio,
    Select,
    Space,
} from "@mantine/core";

import { Button } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

import {
    collectionGroup,
    getDocs,
    getFirestore,
    query,
    where,
    collection,
    orderBy,
    or,
    Timestamp
} from "firebase/firestore";

import {
    CustomClaims,
    Customer,
    KYCStatusCode,
    NotificationMSG,
    RMUser,
    Service,
    ServiceStatusCode
} from "../types";
import { ConfigContext, MyUserDataContext } from "../contexts";
import { formatFinderAddress, getAddressDetailsEnglish } from "../helpers/addressHelpers";
import { customerIdNumber } from "../portfolio_done_summary";
import { DatePickerInput } from "@mantine/dates";
import { fromMantineDate } from "../helpers/mantineDates";
import { loadServiceExportRecords } from "../helpers/serviceExportData";

/** "Male" / "Female" for individuals; "Company", "Trust" or "Other" for everyone else. Empty when unknown. */
const customerGenderOrType = (customer: Pick<Customer, "customerType" | "gender"> | undefined): string => {
    if (!customer) return "";
    const type = customer.customerType ?? "individual";
    if (type !== "individual") return type.charAt(0).toUpperCase() + type.slice(1);
    if (customer.gender === "M") return "Male";
    if (customer.gender === "F") return "Female";
    return "";
};

/** Same "dd/Mon/yyyy" format as the Date column. Empty when missing. */
const formatExportDate = (ts: Timestamp | undefined): string =>
    ts?.toDate?.().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "/") ?? "";

/** "Update only" / "Create only" exports always start from this date (inclusive). */
const KFHTRADE_ONLY_CREATED_FROM = "2026-08-01";

type FormProps = {
    serviceType: string;
    portfolioTypes: string[];
    serviceStatuses: string[];
    kycStatuses: string[];
    paid?: boolean,
    created?: [string | null, string | null],
    riskStatus?: string,
    updateOnly?: boolean,
    createOnly?: boolean,
    exportType: "KCC" | "Internal";
    exportFormat: "xlsx" | "csv";
};

const GenerateServicesExcelModal = (props: any) => {
    const userContext = useContext(MyUserDataContext);
    let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};

    const opened: boolean = props.opened;
    const setOpened = props.setOpened;
    const [confirming, setConfirming] = useState<boolean>(false);
    const [msg, setMsg] = useState<NotificationMSG | null>(null);
    const [downloadReady, setDownloadReady] = useState<boolean>(false);
    const config = useContext(ConfigContext);
    const db = getFirestore();
    const portfolioSelectData = useMemo(() => {
        return Object.entries(config.portfolio_service ?? {})
            // you can filter inactive if you want: .filter(([_, p]) => p.active !== false)
            .sort((a, b) => {
                const pa = a[1].position ?? 0;
                const pb = b[1].position ?? 0;
                return pa - pb;
            })
            .map(([key, portfolio]) => ({
                value: key,
                label: portfolio.name_en || key,
                description: portfolio.description_en,
                whatsappTemplate: portfolio.whatsappTemplate,
            }));
    }, [config.portfolio_service]);
    const headers = [
        "ID",
        "Type",
        "Date",
        "Arabic Name",
        "English Name",
        "Civil ID / Commercial Register",
        "Mobile",
        "Nationality",
        "Gender / Type",
        "Address",
        "Status",
        "Risk Level",
        "Risk Reason"
    ];

    const [data, setData] = useState<any[]>([]);

    const handleSubmitGenerateExcel = async (formData: FormProps) => {
        console.log("Query parameters:", formData);

        const servicesStatusLength = Math.max(formData.serviceStatuses?.length || 0, 1);
        const kycStatusesLength = Math.max(formData.kycStatuses?.length || 0, 1);

        if ((servicesStatusLength * kycStatusesLength) + 1 > 30) {
            setMsg({ type: "error", message: "Too many combinations. Please reduce number of chosen attributes" });
            setConfirming(false);
            return;
            // throw new Error(`Too many combinations. (${(servicesStatusLength * kycStatusesLength * serviceTypesLength) + 1}) Please reduce number of chosen attributes`);
        }

        let q = query(collectionGroup(db, "services"), orderBy("created", "desc"));

        // Filter by service status
        if (formData.serviceStatuses?.length > 0) {
            q = query(q, where("tags.serviceStatus.code", "in", formData.serviceStatuses));
        }
        if (formData.kycStatuses?.length > 0) {
            q = query(q, where("tags.kycStatus.code", "in", formData.kycStatuses));
        }
        const kfhtradeOnlyMode = formData.serviceType === "kfhtrade" && (formData.updateOnly || formData.createOnly);
        if (kfhtradeOnlyMode) {
            // Update only / Create only: always fetch everything created from 1 Aug 2026 onwards
            q = query(q, where("created", ">=", Timestamp.fromDate(fromMantineDate(KFHTRADE_ONLY_CREATED_FROM)!)));
        } else if (formData.created?.length && formData.created[0] && formData.created[1]) {
            q = query(q, where("created", ">=", Timestamp.fromDate(fromMantineDate(formData.created[0])!)));
            q = query(q, where("created", "<=", Timestamp.fromDate(fromMantineDate(formData.created[1])!)));
        }
        if (formData.serviceType === ("portfolio")) {
            if (formData.portfolioTypes.length > 0) {

                q = query(q, where("portfolioOpeningRequest.portfolio_db_id", "in", formData.portfolioTypes));
            }

        }
        if (formData.paid) {
            q = query(q, where("tags.paid.code", "==", "paid"));
        }

        // Filter by service types if needed
        if (formData.serviceType?.length > 0) {
            q = query(q, where("serviceType", "==", formData.serviceType));
        }

        // in portfolio only rely on the refferal segment not the client segment
        if (formData.serviceType == "portfolio") {
            if (userClaims?.availableSegments) {
                q = query(q, (where("referredBy.segment", "in", userClaims.availableSegments)))

            }
            else if (userClaims?.segment) {
                q = query(q, (where("referredBy.segment", "==", userClaims.segment)))

            }
        } else {

            if (userClaims?.availableSegments) {
                q = query(q, or(where("rm.segment", "in", userClaims.availableSegments), where("customerObj.segment", "in", userClaims.availableSegments)))

            }
            else if (userClaims?.segment) {
                q = query(q, or(where("rm.segment", "==", userClaims.segment), where("customerObj.segment", "==", userClaims.segment)))

            }
        }

        const services: any[] = [];

        // Fetch all RMs once so the customer's rm_email resolves to a name/ID
        const rmsByEmail = new Map<string, RMUser>();
        if (formData.exportType === "Internal") {
            const rmsSnapshot = await getDocs(collection(db, "rms"));
            rmsSnapshot.docs.forEach((rmDoc) => {
                const rm = rmDoc.data() as RMUser;
                if (rm.email) rmsByEmail.set(rm.email.toLowerCase(), { ...rm, id: rm.id ?? rmDoc.id });
            });
        }

        for await (const { service, customerData, kycData } of loadServiceExportRecords(db, q, { includeKyc: formData.exportType !== "KCC" })) {
            if (formData.serviceType === "kfhtrade" && formData.updateOnly && !customerData?.kfhtrade_id?.trim()) {
                continue;
            }
            // Create only: skip customers that already have a KFH Trade account
            if (formData.serviceType === "kfhtrade" && formData.createOnly && customerData?.kfhtrade_id?.trim()) {
                continue;
            }
            // Format the row data according to the simplified structure
            let rowData: any = {};
            let riskStatus: string | undefined = "";
            if (formData.exportType === "KCC") {
                // Get birth date from customer data or KYC
                const birthDate = service.paciData?.PersonalData?.BirthDate
                    ? (() => {
                        const date = new Date(service.paciData.PersonalData.BirthDate);
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear().toString();
                        return `${day}${month}${year}`;
                    })()
                    : "?";

                // Get civil ID expiry date
                const civilIDExpiryDate = service.paciData?.PersonalData?.CardExpiryDate
                    ? (() => {
                        const date = new Date(service.paciData.PersonalData.CardExpiryDate);
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear().toString();
                        return `${year}${month}${day}`;  //yes it's flipped.  Don't ask me, ask the moron who defined the template.
                    })()
                    : "?";

                // Get address
                const address = getAddressDetailsEnglish(service.paciData?.PersonalData?.Address || {}) ||
                    "?";
                // const address = "?";
                rowData = {
                    "col-1": (customerData?.name_ar || "?").replace(/-/g, ''), // Long Name(O)
                    "col-2": (customerData?.name_en || "?").replace(/-/g, ''), // Long Name€ no special character
                    "col-3": "INDV", // BP Category
                    "col-4": "CVID", // ID Type
                    "col-5": customerData?.civilID || "?", // ID No
                    "col-6": "KW", // Country of Issue
                    "col-7": civilIDExpiryDate, // Expiry Date
                    "col-8": "Y", // Resident Flag
                    "col-9": address, // Street Address
                    "col-10": "KW", // Country
                    "col-11": "KFH Capital", // Contact Person
                    "col-12": "22987000", // Mobile No
                    "col-13": "Manager", // Designation
                    "col-14": "TRDR", // IC Trader
                    "col-15": "N", // Role
                    "col-16": 908, // Default Bank ID
                    "col-17": "011440001029", // Bank A/C No
                    "col-18": "8", // Risk Level
                    "col-19": "1", // Networth
                    "col-20": "Y", // Actual Beneficiary
                    "col-21": "N", // Relationship with PEP
                    "col-22": "3489", // Source of Income
                    "col-23": birthDate, // Date of Birth
                    "col-24": "OTHR", // Occupation
                    "col-25": "KUWAIT", // Place of Birth
                    "col-26": "1", // Expected Investment
                    "col-27": "1", // Annual Income
                    "col-28": "AMATEUR", // Investment Expertise
                    "col-29": "", // Nature of Corporation
                    "col-30": "22987000", // Telephone No.1
                    "col-31": "OTHR", // EMPLOYMENT_STATUS
                    "col-32": "OTHR", // Employer Name
                    "col-33": "3489", // Purpose of A/C Opening
                    "col-34": "NONE", // Position of the PEP
                    "col-35": "NONE", // Name of the PEP
                    "col-36": "CRIC", // Investment Company ID Type
                    "col-37": "76471", // Investment Company ID No
                    "col-38": "2", // Sex
                    "col-39": "KW", // Citizen
                    "col-40": "KW", // Rounded Balance
                    "col-41": "KW", // Additional field
                    "col-42": customerData?.gender === "F" ? "F" : "M", // Gender
                    "col-43": "N", // Another field
                    "col-44": "Y" // Final field
                };
            } else {
                const customerRM = customerData?.rm_email
                    ? rmsByEmail.get(customerData.rm_email.toLowerCase())
                    : undefined;

                rowData = {
                    "ID": service.id || "",
                    "Type": service.serviceType || "",
                    "Date": service.created?.toDate().toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric"
                    }).replace(/ /g, "/") || "",
                    "Arabic Name": customerData?.name_ar || service.customerObj?.name_ar || "",
                    "English Name": customerData?.name_en || service.customerObj?.name_en || "",
                    "Civil ID / Commercial Register": customerIdNumber(customerData ?? service.customerObj),
                    "Mobile": customerData?.mobile || service.customerObj?.mobile || "",
                    "Nationality": customerData?.nationality || service.customerObj?.nationality || "",
                    "Gender / Type": customerGenderOrType(customerData ?? service.customerObj),
                    "Address": formatFinderAddress(customerData?.address_from_finder ?? service.customerObj?.address_from_finder),
                    "Service Status": (service.tags?.serviceStatus?.code || "").replace(/_/g, " "),
                    "KYC Status": (service.tags?.kycStatus?.code || "").replace(/_/g, " "),
                    "Risk Level": kycData?.state?.risk_code || "",
                    "Customer Segment": customerData?.segment || "",
                    "Customer RM": customerRM?.name_en || customerData?.rm_email || "",
                    "Customer RM ID": customerRM?.id || "",
                    "Risk Reason": kycData?.state?.risk || "",
                    "RM Segment": service?.rm?.segment || "",
                    "RM Email": service?.rm?.email || "",
                    "RM Name": (service?.rm as RMUser)?.name_en || "",
                    // "RM Mobile": (service?.rm as RMUser)?.mobile || "",
                    "RM ID": (service?.rm as RMUser)?.id || "",
                    "Referred By Name": (service?.referredBy as RMUser)?.name_en || "",
                    "Referred By Email": (service?.referredBy as RMUser)?.email || "",
                    "Referred By Segment": (service?.referredBy as RMUser)?.segment || "",
                    "Referred By RM ID": (service?.referredBy as RMUser)?.rm_id || "",

                };
                if (service.serviceType == "portfolio") {
                    //check the transfers for the client by currency 
                    rowData["paid"] = (service as Service)?.tags?.paid ? "yes" : "no"
                    rowData["portfolio Type"] = (service as Service)?.portfolioOpeningRequest?.portfolio_db_id;
                    rowData["portfolio ID"] = (service as Service)?.portfolioOpeningRequest?.portfolioId;
                    rowData["from Mobile"] = (service as Service)?.fromMobile ? "yes" : "no";

                    if ((service as Service).transferInvoices) {
                        const invoicesInKWD = (service as Service).transferInvoices?.filter((invoiceObj) => invoiceObj.currency === "KWD");
                        const invoicesInUSD = (service as Service).transferInvoices?.filter((invoiceObj) => invoiceObj.currency === "USD");
                        rowData["amount (KWD)"] = invoicesInKWD?.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0) || 0;
                        rowData["amount (USD)"] = invoicesInUSD?.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0) || 0;

                        rowData["amount (KWD) [WITHOUT FEES]"] = invoicesInKWD?.reduce((sum, invoice) => sum + (Number(invoice.initialAmount) || 0), 0) || 0;
                        rowData["amount (USD) [WITHOUT FEES]"] = invoicesInUSD?.reduce((sum, invoice) => sum + (Number(invoice.initialAmount) || 0), 0) || 0;


                    } else {
                        rowData["KFH Trade ID"] = customerData?.kfhtrade_id || "";
                    }




                }
                if (customerData?.tags?.risk?.code === "high") {
                    rowData.highRiskReason = kycData?.state?.risk;
                }

                rowData["Civil ID Expiry Date"] = formatExportDate(kycData?.civilIDExpiryDate ?? customerData?.lastValidKYC?.civilIDExpiryDate);
                rowData["Birthdate"] = formatExportDate(kycData?.birthDate);
                rowData["CIF"] = customerData?.crmID || "";
                rowData["Email"] = customerData?.email || customerData?.paciEmail || "";
                rowData["Portfolio Fees - Percentage"] = service.serviceType === "portfolio"
                    ? (service as Service)?.portfolioOpeningRequest?.fees ?? ""
                    : "";

                riskStatus = customerData?.tags?.risk?.code;
                rowData["Risk Level"] = riskStatus;
            }
            if (formData.riskStatus == "high") {
                // only push if the status is high 
                if (riskStatus === "high") {
                    services.push(rowData);
                }
            }
            else if (formData.riskStatus == "low") {
                // only push if the status is low 
                if (riskStatus === "low") {
                    services.push(rowData);
                }
            }
            else {

                services.push(rowData);
            }
        }

        setData(services);
        if (services.length === 0) {
            setMsg({ type: "error", message: "No services found for the selected criteria." });
            setDownloadReady(false)
        } else {
            setMsg({ type: "success", message: `file Created Successfully for ${services.length} services` });
            setDownloadReady(true)
        }

    };

    const downloadExcel = () => {
        if (!data || data.length === 0) return;

        try {
            const exportFormat = form.getValues().exportFormat === "csv" ? "csv" : "xlsx";

            const sheetOptions: any = {};
            if (form.getValues().exportType === "KCC") {
                sheetOptions.skipHeader = true; // skip header row when requested
            }

            // json_to_sheet appends keys in first-seen order, so optional columns (amounts,
            // highRiskReason) missing from the first row would land after these; keep them last.
            const trailingColumns = ["Civil ID Expiry Date", "Birthdate", "CIF", "Email", "Portfolio Fees - Percentage"];
            const allColumns = [...new Set(data.flatMap((row) => Object.keys(row)))];
            sheetOptions.header = [
                ...allColumns.filter((c) => !trailingColumns.includes(c)),
                ...trailingColumns.filter((c) => allColumns.includes(c)),
            ];

            // Build worksheet from your JSON
            const ws = XLSX.utils.json_to_sheet(data, sheetOptions);
            const date = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
            const filename = `services_${date}.${exportFormat}`;

            if (exportFormat === "csv") {
                // Make CSV with all fields quoted (and CRLF for Excel)
                const csv = XLSX.utils.sheet_to_csv(ws, {
                    FS: ",",
                    RS: "\r\n",
                    forceQuotes: true,
                });

                // Add BOM so Excel opens Arabic/UTF-8 correctly
                const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            } else {
                // XLSX path: create workbook and save
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Services");
                XLSX.writeFile(wb, filename, { bookType: "xlsx" });
            }

            console.log("file downloaded successfully");
        } catch (error) {
            console.error("Error downloading file:", error);
            setMsg({ type: "error", message: "Error downloading file. Please try again." });
        }
    };

    const form = useForm<FormProps>({
        initialValues: {

            serviceType: "portfolio",
            portfolioTypes: [],
            riskStatus: "all",
            serviceStatuses: [],
            kycStatuses: [],
            updateOnly: false,
            createOnly: false,
            exportType: "Internal",
            exportFormat: "xlsx"
        }
    });


    return (
        <>
            <Modal
                opened={opened}
                onClose={() => {
                    setMsg(null);
                    setOpened(false);
                    setConfirming(false);
                    setDownloadReady(false);
                    setData([]);
                }}
                title="Generate Services Excel"
            >
                <form
                    onSubmit={form.onSubmit(async (values) => {
                        console.log("Form values:", values);

                        setDownloadReady(false);
                        setData([]);
                        setMsg(null);
                        setConfirming(true);

                        try {
                            await handleSubmitGenerateExcel(values);

                        } catch (error: any) {
                            console.error("Error generating Excel:", error);
                            setMsg({ type: "error", message: error.message });
                        }

                        setConfirming(false);
                    })}
                >
                    <MultiSelect
                        {...form.getInputProps("serviceStatuses")}
                        label="Select service status tags"
                        placeholder="All statuses"
                        data={
                            Object.keys(ServiceStatusCode).map(code => {
                                return {
                                    value: code,
                                    label: code.replace(/_/g, " ")
                                };
                            })
                        }
                    />
                    <MultiSelect
                        {...form.getInputProps("kycStatuses")}
                        label="Select kyc status tags"
                        placeholder="All statuses"
                        data={
                            Object.keys(KYCStatusCode).map(code => {
                                return {
                                    value: `kyc_${code}`,
                                    label: code.replace(/_/g, " ")
                                };
                            })
                        }
                    />

                    <Select
                        {...form.getInputProps("riskStatus")}
                        label="Select Risk status tags"
                        placeholder="Customer Risk status (low/high)"
                        data={
                            ["high", "low", "all"]
                        }
                    />

                    <DatePickerInput
                        type="range"
                        label="Created Range "
                        disabled={form.values.serviceType === "kfhtrade" && (form.values.updateOnly || form.values.createOnly)}
                        description={form.values.serviceType === "kfhtrade" && (form.values.updateOnly || form.values.createOnly)
                            ? `${form.values.updateOnly ? "Update" : "Create"} only exports all services created from 1 Aug 2026 onwards`
                            : undefined}
                        {...form.getInputProps("created")}
                    />

                    <Select
                        {...form.getInputProps("serviceType")}
                        label="Select service types"
                        placeholder="All service types"
                        data={[
                            { value: "kfhtrade", label: "KFH Trade" },
                            { value: "kfhbrokerage", label: "KFH Brokerage" },
                            { value: "portfolio", label: "Portfolio" },
                            { value: "ipo", label: "IPO" }
                        ]}
                    />
                    <Space h="md" />

                    {form.values.serviceType === ("portfolio") && <>
                        <MultiSelect
                            {...form.getInputProps("portfolioTypes")}
                            label="Select Portfolio Type"
                            placeholder="All Portfolio Type"
                            data={portfolioSelectData}
                        />
                        <Space h="md" />
                        <Checkbox
                            checked={form.values.paid}
                            disabled={false}
                            onChange={(e) => {
                                form.setFieldValue("paid", e.target.checked);
                            }}
                            label="Paid only"
                        />
                        <Space h="md" />
                    </>}

                    {form.values.serviceType === ("kfhtrade") && <Group>
                        <Checkbox
                            checked={form.values.updateOnly}
                            disabled={false}
                            onChange={(e) => {
                                form.setFieldValue("updateOnly", e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldValue("createOnly", false);
                                }
                                // Update only always starts from 1 Aug 2026; reset the range when unchecked
                                form.setFieldValue("created", e.target.checked ? [KFHTRADE_ONLY_CREATED_FROM, null] : [null, null]);
                            }}
                            label="Update only"
                        />
                        <Checkbox
                            checked={form.values.createOnly}
                            disabled={false}
                            onChange={(e) => {
                                form.setFieldValue("createOnly", e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldValue("updateOnly", false);
                                }
                                // Create only always starts from 1 Aug 2026; reset the range when unchecked
                                form.setFieldValue("created", e.target.checked ? [KFHTRADE_ONLY_CREATED_FROM, null] : [null, null]);
                            }}
                            label="Create only"
                        />
                    </Group>}

                    <Space h="md" />

                    <Radio.Group

                        label="Export Type"
                        {...form.getInputProps("exportType")}
                    >
                        <Group mt="xs">
                            <Radio value={"KCC"} label="KCC" />
                            <Radio value={"Internal"} label="Internal" defaultChecked />
                        </Group>
                    </Radio.Group>
                    <Radio.Group

                        label="Export Fotmat"
                        {...form.getInputProps("exportFormat")}
                    >
                        <Group mt="xs">
                            <Radio value={"xlsx"} label="XLSX" defaultChecked />
                            <Radio value={"csv"} label="CSV" />
                        </Group>
                    </Radio.Group>

                    <Space h="xl" />

                    <Group>
                        <Button color="grape" type="submit" loading={confirming}>
                            Generate File
                        </Button>
                    </Group>

                    <Space h="xl" />

                    {msg && (
                        <Alert
                            icon={<IconAlertCircle size={16} />}
                            color={msg.type === "success" ? "green" : "red"}
                            title={msg.type === "success" ? "Success" : "Failure"}
                        >
                            {msg.message}
                            {downloadReady && (
                                <>
                                    <br />
                                    <Button
                                        size="xs"
                                        variant="light"
                                        onClick={downloadExcel}
                                        mt="sm"
                                    >
                                        Download File
                                    </Button>
                                </>
                            )}
                        </Alert>
                    )}
                </form>
            </Modal>
        </>
    );
};

export default GenerateServicesExcelModal;
